import {
  PlaycastMetricPayload,
  send as sendMetric,
  type PlaycastMetricPlayerChangeMode,
} from '@playcastdotio/Metrics';

import config from '@playcastdotio/GlobalConfig';
import { getPersistentPlayerId } from './api';
import { playerNameState, joinState, hostConnectionState, authStateStore, joinIdState, invitationState2 } from './api/stores';
import { PeerQualityRecommendation, PlaycastWebrtcQualityRawStats } from '@playcastdotio/CoreTypes';
import { sendAnalytics } from '@playcastdotio/Analytics';

const persistentId = getPersistentPlayerId();
let playerName = playerNameState.currentState.name;
playerNameState.addListener((state) => {
  playerName = state.name;
});

let notifiedOfAuth = false;

// wrapper to include the persistentId and auth token in the headers for metrics requests
const send = async (data: PlaycastMetricPayload): Promise<boolean> => {
  const headers = {
    persistentId,
  };
  if (authStateStore.currentState.accessToken) {
    headers['Authorization'] = `Bearer ${authStateStore.currentState.accessToken}`;
  }

  try {
    console.log('send metric', data, headers);
    await sendMetric(data, headers);
    return true;
  } catch (e) {
    console.error('[METRICS] Failed to send metric', e);
    return false;
  }
};

export const sendMetricHostInstallDownload = (userAgent: string, ipAddress: string) => {
  
  sendAnalytics({
    name: 'host-install-download',
    role: 'host',
    persistentId,
    userAgent,
    ipAddress,
  });

};

export const sendMetricPlayerAuthenticate = (playerAuthId: string) => {
  
  if (!notifiedOfAuth) {
    notifiedOfAuth = true;
    sendAnalytics({
      name: 'player-authenticate',
      role: 'player',
      playerAuthId,
      persistentId,
    });
  }
};

export const sendMetricPlayerVisitInvite = () => {
  const playerAuthId = authStateStore.currentState.playerAuthId;
  const inviteId = invitationState2.currentState.selectedInvite?.id ?? '';

  sendAnalytics({
    name: 'player-visit-invite',
    role: 'player',
    playerAuthId,
    persistentId,
    inviteId,
  });
};

export const sendMetricPlayerVisitAlias = () => {
  const alias = joinIdState.currentState;
  const playerAuthId = authStateStore.currentState.playerAuthId;

  sendAnalytics({
    name: 'player-visit-alias',
    role: 'player',
    playerAuthId,
    persistentId,
    alias,
  });

};

export const sendMetricPlayerVisitDeck = () => {
  console.warn('sendMetricPlayerVisitDeck not implemented');
  // const deckId = deckState.currentState.selectedDeck || '';

  // const playerAuthId = authStateStore.currentState.playerAuthId;
  // const data: PlaycastMetricPlayerVisitDeck = {
  //   name: 'player-visit-deck',
  //   version: config.VERSION,
  //   environment: config.ENVIRONMENT,
  //   playerAuthId,
  //   persistentId,
  //   deckId,
  // };
  // return send(data);
};

export const sendMetricPlayerAcceptInvite = () => {
  // const inviteId = joinIdState.currentState;
  const inviteId = invitationState2.currentState.selectedInvite?.id ?? '';
  const playerAuthId = authStateStore.currentState.playerAuthId;

  sendAnalytics({
    name: 'player-accept-invite',
    role: 'player',
    playerAuthId,
    persistentId,
    playerName,
    inviteId,
  });
};

export const initListeners = () => {
  joinState.addListener((state) => {
    if (state.state === 'resolvingAlias') {
      sendMetricPlayerVisitAlias();
    }
    if (state.state === 'chooseInvite') {
      sendMetricPlayerVisitInvite();
    }
    if (state.state === 'chooseDeck') {
      sendMetricPlayerVisitDeck();
    }
    if (state.state === 'connecting') {
      sendMetricPlayerAcceptInvite();
    }
  });
};

export const sendMetricPlayerChangeMode = (mode: PlaycastMetricPlayerChangeMode['mode']) => {
  const broadcastSessionId = hostConnectionState.currentState.broadcastSessionId;
  const playerAuthId = authStateStore.currentState.playerAuthId ?? '';
  const inviteId = invitationState2.currentState.selectedInvite?.id ?? '';
  const playerConnectionId: string = hostConnectionState.currentState.playerPeerId ?? '';

  sendAnalytics({
    name: 'player-change-mode',
    role: 'player',
    playerAuthId,
    persistentId,
    playerConnectionId,
    playerName,
    broadcastSessionId,
    mode,
    inviteId,
  });
};

export const sendMetricPlayerFrameratePanic = (hardReset: boolean, reason: 'fps' | 'bitrate') => {
  const broadcastSessionId = hostConnectionState.currentState.broadcastSessionId;
  const playerAuthId = authStateStore.currentState.playerAuthId ?? '';
  const inviteId = invitationState2.currentState.selectedInvite?.id ?? '';
  const playerConnectionId: string = hostConnectionState.currentState.playerPeerId ?? '';

  sendAnalytics({
    name: 'player-framerate-panic',
    role: 'player',
    broadcastSessionId,
    playerAuthId,
    persistentId,
    playerConnectionId,
    inviteId,
    hardReset,
    reason,
  });
};

export type WebrtcQualityStatsMetric = {
  recommendation: PeerQualityRecommendation;
  reason: string;
  rawStats: PlaycastWebrtcQualityRawStats;
};
export const sendMetricPlayerBatchWebrtcStats = async (stats: WebrtcQualityStatsMetric[], interval: number) => {
  // sanitize the stats so we dont have any null, undefined, or NaN values.
  const sanitizedStats = stats.map((stat) => {
    const sanitizedRawStats = { ...stat.rawStats };
    Object.keys(sanitizedRawStats).forEach((key) => {
      if (typeof sanitizedRawStats[key] === 'number' && isNaN(sanitizedRawStats[key])) {
        sanitizedRawStats[key] = 0;
      }
      if (typeof sanitizedRawStats[key] === 'object' && sanitizedRawStats[key] !== null) {
        Object.keys(sanitizedRawStats[key]).forEach((subKey) => {
          if (typeof sanitizedRawStats[key][subKey] === 'number' && isNaN(sanitizedRawStats[key][subKey])) {
            sanitizedRawStats[key][subKey] = 0;
          }
        });
      }
      if (Array.isArray(sanitizedRawStats[key])) {
        sanitizedRawStats[key] = sanitizedRawStats[key].map((val) => {
          if (typeof val === 'number' && isNaN(val)) {
            return 0;
          }
          return val;
        });
      }
    });
    return {
      ...stat,
      rawStats: sanitizedRawStats,
    };
  });

  const broadcastSessionId = hostConnectionState.currentState.broadcastSessionId;
  const playerAuthId = authStateStore.currentState.playerAuthId ?? '';
  const inviteId = invitationState2.currentState.selectedInvite?.id ?? '';
  const playerConnectionId: string = hostConnectionState.currentState.playerPeerId ?? '';

  sendAnalytics({
    name: 'webrtc-quality-stats',
    role: 'player',
    broadcastSessionId,
    playerAuthId,
    persistentId,
    playerConnectionId,
    inviteId,
    qualityStats: sanitizedStats,
    collectionInterval: interval,
  });

};
